/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { uploadDocumentToR2 } from '../lib/r2Storage';
import { WAREHOUSES_LIST as DEFAULT_WAREHOUSES } from '../lib/constants';
import { ReceiptOcrModal } from '../components/ReceiptOcrModal';
import { downloadTextFile } from '../lib/receiptOcrHelper';
import { parseLocalOrderJson } from '../lib/aiServiceClient';

const StockInReceiptPage = ({
  supabase,
  itemMaster = [],
  supplies = [],
  WAREHOUSES_LIST = DEFAULT_WAREHOUSES,
  setSuccessModal,
  fetchData = () => {},
  user,
  isMobile = false,
  onNavigateToInspection
}) => {
  // Navigation Tabs
  const [activeTab, setActiveTab] = useState('entry'); // 'entry' | 'history'
  const [recentlyCreatedReceipt, setRecentlyCreatedReceipt] = useState(null);

  // ==========================================
  // Form State (New Receipt Entry)
  // ==========================================
  const [header, setHeader] = useState({
    receipt_number: '',
    po_number: '',
    supplier_name: '',
    received_date: new Date().toISOString().split('T')[0],
    warehouse_name: 'คลังใหญ่ (คลังพัสดุ)',
    note: ''
  });

  const [receiptImage, setReceiptImage] = useState('');
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [isOcrModalOpen, setIsOcrModalOpen] = useState(false);
  const [ocrText, setOcrText] = useState('');
  const [importedEngine, setImportedEngine] = useState(null);

  // 1:N Items
  const createEmptyItem = () => ({
    id: Date.now() + Math.random(),
    master_id: null,
    item_name: '',
    lot_number: '',
    exp_date: '',
    mfg_date: '',
    quantity: 1,
    unit: 'กล่อง',
    unit_price: 0
  });

  const [items, setItems] = useState([createEmptyItem()]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ==========================================
  // History & Archive State
  // ==========================================
  const [receiptsList, setReceiptsList] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historySearchQuery, setHistorySearchQuery] = useState('');
  const [selectedReceiptDetail, setSelectedReceiptDetail] = useState(null);

  // Image Zoom Modal
  const [zoomedImageUrl, setZoomedImageUrl] = useState(null);

  // Filtered warehouses list (selectable destinations)
  const selectableWarehouses = useMemo(() => {
    return (WAREHOUSES_LIST || []).filter(w => {
      const name = w.name.trim();
      return !name.startsWith('-') || name.includes('ห้อง sterile') || name.includes('คลัง');
    }).map(w => w.name.trim().replace(/^-\s*/, ''));
  }, [WAREHOUSES_LIST]);

  // Distinct supplier suggestions from itemMaster
  const supplierSuggestions = useMemo(() => {
    const suppliers = new Set();
    itemMaster.forEach(m => {
      if (m.company && m.company.trim()) {
        suppliers.add(m.company.trim());
      }
    });
    return Array.from(suppliers);
  }, [itemMaster]);

  // Unique item names for autocomplete
  const itemNameSuggestions = useMemo(() => {
    const names = new Set();
    itemMaster.forEach(m => {
      if (m.item_name && m.item_name.trim()) {
        names.add(m.item_name.trim());
      }
    });
    return Array.from(names);
  }, [itemMaster]);

  // ==========================================
  // Image Upload Handler
  // ==========================================
  const handleImageFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('กรุณาเลือกไฟล์รูปภาพที่ถูกต้อง');
      return;
    }

    setIsUploadingImage(true);
    setUploadError('');
    try {
      const uploadedUrl = await uploadDocumentToR2(file, 'receipts', {
        maxWidth: 2560,
        maxHeight: 2560,
        quality: 0.88
      });
      setReceiptImage(uploadedUrl);
    } catch (err) {
      console.error('Failed to upload receipt image:', err);
      setUploadError('อัปโหลดรูปล้มเหลว กรุณาลองใหม่อีกครั้ง');
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleDownloadReceiptImage = async (imageUrl, filename = 'receipt-evidence.jpg') => {
    if (!imageUrl) return;
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Direct download failed, opening in new tab:', err);
      window.open(imageUrl, '_blank');
    }
  };

  const handleRemoveImage = () => {
    setReceiptImage('');
    setUploadError('');
    setOcrText('');
  };

  const handleApplyOcrData = (data) => {
    if (!data) return;
    if (data.engine) {
      setImportedEngine(data.engine);
    }
    setHeader(prev => ({
      ...prev,
      receipt_number: data.receiptNumber || prev.receipt_number,
      po_number: data.poNumber || prev.po_number,
      supplier_name: data.supplierName || prev.supplier_name,
      received_date: data.receivedDate || prev.received_date
    }));
    setOcrText(data.rawText || '');

    let itemsCount = 0;
    if (data.detectedItems && data.detectedItems.length > 0) {
      const populatedItems = data.detectedItems.map(item => {
        const rawName = (item.item_name || '').trim();
        const matchedMaster = itemMaster.find(m =>
          m.item_name && m.item_name.trim().toLowerCase() === rawName.toLowerCase()
        );

        return {
          id: Date.now() + Math.random(),
          master_id: matchedMaster ? matchedMaster.id : null,
          item_name: item.item_name || '',
          lot_number: item.lot_number || '',
          exp_date: item.exp_date || '',
          mfg_date: item.mfg_date || '',
          quantity: Number(item.quantity) || 1,
          unit: item.unit || (matchedMaster?.unit ? matchedMaster.unit : 'กล่อง'),
          unit_price: Number(item.unit_price) || (matchedMaster?.price_per_unit ? Number(matchedMaster.price_per_unit) : 0)
        };
      });

      setItems(populatedItems);
      itemsCount = populatedItems.length;
    }

    const isLocalServer = data.engine === 'local_server' || data.engine === 'synology-nas' || data.engine === 'server' || importedEngine === 'local_server';
    const sourceName = isLocalServer ? 'Local AI Server (Mac M4 / NAS)' : 'OCR';
    const message = itemsCount > 0
      ? `✅ นำข้อมูลหัวบิลและรายการสินค้า (${itemsCount} รายการ) จาก ${sourceName} ไปกรอกในฟอร์มเรียบร้อยแล้ว`
      : `✅ นำข้อมูลจาก ${sourceName} ไปกรอกในฟอร์มเรียบร้อยแล้ว`;

    alert(message);
    if (typeof setSuccessModal === 'function') {
      setSuccessModal({
        title: isLocalServer ? '⚡ นำเข้าข้อมูล Local AI สำเร็จ' : 'นำข้อมูลสำเร็จ ✨',
        message
      });
    }
  };

  const handleJsonFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target?.result;
        const parsed = parseLocalOrderJson(text);
        setImportedEngine(parsed.engine || 'local_server');
        handleApplyOcrData(parsed);
      } catch (err) {
        alert('นำเข้าไฟล์ JSON ไม่สำเร็จ: ' + err.message);
      }
    };
    reader.onerror = () => {
      alert('เกิดข้อผิดพลาดในการอ่านไฟล์ JSON');
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  // ==========================================
  // Items Row Operations
  // ==========================================
  const handleAddItem = () => {
    setItems(prev => [...prev, createEmptyItem()]);
  };

  const handleRemoveItem = (index) => {
    if (items.length <= 1) {
      setItems([createEmptyItem()]);
      return;
    }
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    setItems(prev => {
      const next = [...prev];
      const row = { ...next[index], [field]: value };

      // Autocomplete if selecting or typing matching item_name
      if (field === 'item_name') {
        const matched = itemMaster.find(
          m => m.item_name.trim().toLowerCase() === String(value).trim().toLowerCase()
        );
        if (matched) {
          row.master_id = matched.id;
          if (matched.unit && (!row.unit || row.unit === 'กล่อง')) {
            row.unit = matched.unit;
          }
          if (matched.price_per_unit && Number(row.unit_price) === 0) {
            row.unit_price = matched.price_per_unit;
          }
        }
      }

      next[index] = row;
      return next;
    });
  };

  // Grand Total Calculation
  const grandTotal = useMemo(() => {
    return items.reduce((sum, item) => {
      const qty = Number(item.quantity) || 0;
      const price = Number(item.unit_price) || 0;
      return sum + (qty * price);
    }, 0);
  }, [items]);

  const totalItemQuantity = useMemo(() => {
    return items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  }, [items]);

  // ==========================================
  // Submit Handler
  // ==========================================
  const handleSubmitReceipt = async (e) => {
    e.preventDefault();
    if (!header.receipt_number.trim()) {
      alert('กรุณากรอกเลขที่ใบเสร็จ / ใบส่งของ');
      return;
    }

    // Validate items
    const validItems = items.filter(it => it.item_name && it.item_name.trim());
    if (validItems.length === 0) {
      alert('กรุณาระบุรายการวัสดุอย่างน้อย 1 รายการ');
      return;
    }

    for (const it of validItems) {
      if (Number(it.quantity) <= 0) {
        alert(`รายการ "${it.item_name}" ต้องมีจำนวนมากกว่า 0`);
        return;
      }
    }

    setIsSubmitting(true);
    try {
      // 1. Insert into receipts table
      const cleanNote = header.note?.trim() || '';
      const noteWithOcr = [
        cleanNote,
        ocrText ? `[OCR Text]:\n${ocrText}` : ''
      ].filter(Boolean).join('\n\n');

      const receiptPayload = {
        receipt_number: header.receipt_number.trim(),
        po_number: header.po_number?.trim() || null,
        supplier_name: header.supplier_name?.trim() || null,
        received_date: header.received_date || new Date().toISOString().split('T')[0],
        total_amount: grandTotal,
        receipt_image_url: receiptImage || null,
        ocr_text: ocrText || null,
        note: noteWithOcr || null,
        created_by: user?.email || 'Admin',
        status: 'completed'
      };

      const { data: insertedReceipt, error: receiptError } = await supabase
        .from('receipts')
        .insert([receiptPayload])
        .select()
        .single();

      if (receiptError) throw receiptError;

      const receiptId = insertedReceipt.id;
      const targetWh = header.warehouse_name || 'คลังใหญ่ (คลังพัสดุ)';

      // 2. Process each item
      for (const it of validItems) {
        let masterId = it.master_id;

        // Try to find matching master item with same name and lot if not set
        if (!masterId) {
          const matchedByName = itemMaster.find(m =>
            m.item_name.trim().toLowerCase() === it.item_name.trim().toLowerCase() &&
            (it.lot_number ? m.lot_number === it.lot_number : true)
          );

          if (matchedByName) {
            masterId = matchedByName.id;
          } else {
            // Create a new entry in item_master for tracking
            const { data: newMaster, error: masterErr } = await supabase
              .from('item_master')
              .insert([{
                item_name: it.item_name.trim(),
                category: 'วัสดุสิ้นเปลือง',
                lot_number: it.lot_number?.trim() || null,
                exp_date: it.exp_date || null,
                unit: it.unit?.trim() || 'ชิ้น',
                price_per_unit: Number(it.unit_price) || 0,
                company: header.supplier_name?.trim() || '',
                po_number: header.po_number?.trim() || '',
                receive_date: header.received_date || null,
                min_stock: 0,
                global_min_stock: 0,
                is_set: false
              }])
              .select()
              .single();

            if (!masterErr && newMaster) {
              masterId = newMaster.id;
            }
          }
        }

        // Insert into receipt_items
        const lineTotal = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
        const itemPayload = {
          receipt_id: receiptId,
          master_id: masterId || null,
          item_name: it.item_name.trim(),
          lot_number: it.lot_number?.trim() || null,
          exp_date: it.exp_date || null,
          mfg_date: it.mfg_date || null,
          quantity: Number(it.quantity) || 0,
          unit: it.unit?.trim() || 'ชิ้น',
          unit_price: Number(it.unit_price) || 0,
          total_price: lineTotal,
          warehouse_name: targetWh
        };

        const { error: lineItemErr } = await supabase
          .from('receipt_items')
          .insert([itemPayload]);

        if (lineItemErr) {
          console.error('Error inserting receipt item:', lineItemErr);
        }

        // Update / Insert into supplies table
        if (masterId) {
          const existingSupply = supplies.find(
            s => s.master_id === masterId && s.warehouse_name === targetWh
          );

          if (existingSupply) {
            const updatedAmount = (Number(existingSupply.item_amount) || 0) + Number(it.quantity);
            await supabase
              .from('supplies')
              .update({
                item_amount: updatedAmount,
                last_updated: new Date().toISOString()
              })
              .eq('id', existingSupply.id);
          } else {
            await supabase
              .from('supplies')
              .insert([{
                master_id: masterId,
                warehouse_name: targetWh,
                item_amount: Number(it.quantity),
                last_updated: new Date().toISOString()
              }]);
          }
        }

        // Log transaction to logs table
        const actionLog = `รับเข้าสินค้าตามใบเสร็จ #${header.receipt_number} จำนวน ${it.quantity} ${it.unit || 'ชิ้น'}${it.lot_number ? ` (Lot: ${it.lot_number})` : ''} เข้า ${targetWh}`;
        await supabase.from('logs').insert([{
          item_name: it.item_name.trim(),
          warehouse: targetWh,
          action: actionLog
        }]);
      }

      // Reset form
      const savedReceiptNumber = header.receipt_number;
      setRecentlyCreatedReceipt({ id: receiptId, number: savedReceiptNumber });

      setHeader({
        receipt_number: '',
        po_number: '',
        supplier_name: '',
        received_date: new Date().toISOString().split('T')[0],
        warehouse_name: 'คลังใหญ่ (คลังพัสดุ)',
        note: ''
      });
      setReceiptImage('');
      setOcrText('');
      setItems([createEmptyItem()]);

      if (typeof setSuccessModal === 'function') {
        setSuccessModal({
          title: 'บันทึกการรับเข้าสำเร็จ! 🎉',
          message: `บันทึกใบเสร็จเลขที่ #${savedReceiptNumber} จำนวน ${validItems.length} รายการ เข้าสต็อก "${targetWh}" เรียบร้อยแล้ว`,
          onClose: () => {
            fetchData();
            fetchReceiptsHistory();
            if (onNavigateToInspection) {
              onNavigateToInspection(receiptId);
            }
          }
        });
      } else {
        alert(`บันทึกใบเสร็จ #${savedReceiptNumber} สำเร็จ!`);
        fetchData();
        fetchReceiptsHistory();
        if (onNavigateToInspection) {
          onNavigateToInspection(receiptId);
        }
      }
    } catch (err) {
      console.error('Submit receipt error:', err);
      alert('เกิดข้อผิดพลาดในการบันทึกใบเสร็จ: ' + (err.message || 'โปรดตรวจสอบสิทธิ์และการเชื่อมต่อ'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // ==========================================
  // Fetch History
  // ==========================================
  const fetchReceiptsHistory = useCallback(async () => {
    setIsLoadingHistory(true);
    try {
      const { data, error } = await supabase
        .from('receipts')
        .select(`
          *,
          receipt_items (*)
        `)
        .order('received_date', { ascending: false })
        .limit(100);

      if (error) throw error;
      setReceiptsList(data || []);
    } catch (err) {
      console.error('Fetch receipts history error:', err);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [supabase]);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchReceiptsHistory();
    }
  }, [activeTab, fetchReceiptsHistory]);

  // Filtered history
  const filteredReceipts = useMemo(() => {
    if (!historySearchQuery.trim()) return receiptsList;
    const q = historySearchQuery.toLowerCase().trim();
    return receiptsList.filter(rc => {
      const numMatch = rc.receipt_number?.toLowerCase().includes(q);
      const poMatch = rc.po_number?.toLowerCase().includes(q);
      const supMatch = rc.supplier_name?.toLowerCase().includes(q);
      const noteMatch = rc.note?.toLowerCase().includes(q);
      const ocrMatch = rc.ocr_text?.toLowerCase().includes(q);
      const itemsMatch = rc.receipt_items?.some(it =>
        it.item_name?.toLowerCase().includes(q) || it.lot_number?.toLowerCase().includes(q)
      );
      return numMatch || poMatch || supMatch || noteMatch || ocrMatch || itemsMatch;
    });
  }, [receiptsList, historySearchQuery]);

  return (
    <div style={{ maxWidth: '1280px', margin: '0 auto', padding: isMobile ? '12px' : '24px' }}>
      {/* Page Title & Navigation Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #0ba360 0%, #3cba92 100%)',
        borderRadius: '16px',
        padding: isMobile ? '16px' : '24px',
        color: '#ffffff',
        marginBottom: '20px',
        boxShadow: '0 10px 25px -5px rgba(11, 163, 96, 0.3)',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '15px'
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: isMobile ? '1.4em' : '1.8em', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>📥</span> ระบบรับเข้าสินค้า & ใบเสร็จ
          </h1>
          <p style={{ margin: '6px 0 0 0', opacity: 0.9, fontSize: isMobile ? '0.85em' : '0.95em' }}>
            บันทึกรับเข้าวัสดุการแพทย์ แนบรูปใบเสร็จเพื่อการตรวจสอบ ตรวจจับ Lot/EXP และอัปเดตสต็อกอัตโนมัติ
          </p>
        </div>

        {/* Tab Switcher */}
        <div style={{
          display: 'flex',
          background: 'rgba(255, 255, 255, 0.2)',
          borderRadius: '10px',
          padding: '4px',
          backdropFilter: 'blur(10px)'
        }}>
          <button
            type="button"
            onClick={() => setActiveTab('entry')}
            style={{
              padding: '8px 18px',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '0.92em',
              transition: 'all 0.2s',
              background: activeTab === 'entry' ? '#ffffff' : 'transparent',
              color: activeTab === 'entry' ? '#0ba360' : '#ffffff'
            }}
          >
            📥 บันทึกรับเข้าตามใบเสร็จ
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('history')}
            style={{
              padding: '8px 18px',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '0.92em',
              transition: 'all 0.2s',
              background: activeTab === 'history' ? '#ffffff' : 'transparent',
              color: activeTab === 'history' ? '#0ba360' : '#ffffff'
            }}
          >
            📑 ประวัติใบเสร็จและการตรวจรับ
          </button>
          {onNavigateToInspection && (
            <button
              type="button"
              onClick={() => onNavigateToInspection()}
              style={{
                padding: '8px 18px',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '0.92em',
                transition: 'all 0.2s',
                background: 'transparent',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              📋 สรุปตรวจรับ & QR
            </button>
          )}
        </div>
      </div>

      {/* แถบแจ้งเตือนเมื่อเพิ่งบันทึกใบเสร็จเสร็จสิ้น */}
      {recentlyCreatedReceipt && (
        <div
          style={{
            background: '#f0f9ff',
            border: '1px solid #bae6fd',
            borderRadius: '12px',
            padding: '14px 18px',
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '10px'
          }}
        >
          <div style={{ color: '#0369a1', fontWeight: '500' }}>
            🎉 บันทึกใบเสร็จเลขที่ <strong>#{recentlyCreatedReceipt.number}</strong> เข้าสต็อกเรียบร้อยแล้ว
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {onNavigateToInspection && (
              <button
                type="button"
                onClick={() => onNavigateToInspection(recentlyCreatedReceipt.id)}
                style={{
                  backgroundColor: '#0284c7',
                  color: '#ffffff',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  fontSize: '0.88em',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                📋 สรุปตรวจรับ & ออก QR
              </button>
            )}
            <button
              type="button"
              onClick={() => setRecentlyCreatedReceipt(null)}
              style={{
                background: 'none',
                border: 'none',
                color: '#64748b',
                cursor: 'pointer',
                fontSize: '1.1em',
                padding: '4px 8px'
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* TAB 1: บันทึกรับเข้าตามใบเสร็จ (Entry Form) */}
      {/* ============================================================ */}
      {activeTab === 'entry' && (
        <form onSubmit={handleSubmitReceipt}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 340px',
            gap: '20px',
            marginBottom: '20px'
          }}>
            {/* Left Column: Header Information */}
            <div style={{
              background: '#ffffff',
              borderRadius: '16px',
              padding: isMobile ? '16px' : '24px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
            }}>
              <h2 style={{
                fontSize: '1.15em',
                color: '#1e293b',
                marginTop: 0,
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                borderBottom: '1px solid #f1f5f9',
                paddingBottom: '10px'
              }}>
                <span>📋</span> ข้อมูลใบเสร็จ / ใบส่งมอบสินค้า
              </h2>

              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                gap: '16px'
              }}>
                {/* Receipt Number */}
                <div>
                  <label style={{ display: 'block', fontWeight: 'bold', fontSize: '0.9em', color: '#334155', marginBottom: '6px' }}>
                    เลขที่ใบเสร็จ / ใบส่งของ <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="เช่น INV-2026-0089"
                    value={header.receipt_number}
                    onChange={(e) => setHeader(prev => ({ ...prev, receipt_number: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1.5px solid #cbd5e1',
                      fontSize: '0.95em',
                      boxSizing: 'border-box',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                  />
                </div>

                {/* PO Number */}
                <div>
                  <label style={{ display: 'block', fontWeight: 'bold', fontSize: '0.9em', color: '#334155', marginBottom: '6px' }}>
                    เลขที่ใบสั่งซื้อ (PO No.)
                  </label>
                  <input
                    type="text"
                    placeholder="เช่น PO-69012"
                    value={header.po_number}
                    onChange={(e) => setHeader(prev => ({ ...prev, po_number: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1.5px solid #cbd5e1',
                      fontSize: '0.95em',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                {/* Supplier Name */}
                <div>
                  <label style={{ display: 'block', fontWeight: 'bold', fontSize: '0.9em', color: '#334155', marginBottom: '6px' }}>
                    บริษัท / ผู้จัดจำหน่าย (Supplier)
                  </label>
                  <input
                    type="text"
                    list="supplier-suggestions"
                    placeholder="พิมพ์หรือเลือกชื่อผู้จำหน่าย"
                    value={header.supplier_name}
                    onChange={(e) => setHeader(prev => ({ ...prev, supplier_name: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1.5px solid #cbd5e1',
                      fontSize: '0.95em',
                      boxSizing: 'border-box'
                    }}
                  />
                  <datalist id="supplier-suggestions">
                    {supplierSuggestions.map((sup, idx) => (
                      <option key={idx} value={sup} />
                    ))}
                  </datalist>
                </div>

                {/* Received Date */}
                <div>
                  <label style={{ display: 'block', fontWeight: 'bold', fontSize: '0.9em', color: '#334155', marginBottom: '6px' }}>
                    วันที่ตรวจรับ
                  </label>
                  <input
                    type="date"
                    value={header.received_date}
                    onChange={(e) => setHeader(prev => ({ ...prev, received_date: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1.5px solid #cbd5e1',
                      fontSize: '0.95em',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                {/* Destination Warehouse */}
                <div>
                  <label style={{ display: 'block', fontWeight: 'bold', fontSize: '0.9em', color: '#334155', marginBottom: '6px' }}>
                    คลังปลายทางที่นำเข้า
                  </label>
                  <select
                    value={header.warehouse_name}
                    onChange={(e) => setHeader(prev => ({ ...prev, warehouse_name: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1.5px solid #cbd5e1',
                      fontSize: '0.95em',
                      boxSizing: 'border-box',
                      backgroundColor: '#f8fafc'
                    }}
                  >
                    {selectableWarehouses.map((wh, idx) => (
                      <option key={idx} value={wh}>{wh}</option>
                    ))}
                  </select>
                </div>

                {/* Notes */}
                <div>
                  <label style={{ display: 'block', fontWeight: 'bold', fontSize: '0.9em', color: '#334155', marginBottom: '6px' }}>
                    หมายเหตุการตรวจรับ
                  </label>
                  <input
                    type="text"
                    placeholder="เช่น ครบถ้วนตามใบสั่ง, กล่องสภาพสมบูรณ์"
                    value={header.note}
                    onChange={(e) => setHeader(prev => ({ ...prev, note: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1.5px solid #cbd5e1',
                      fontSize: '0.95em',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Right Column: Receipt Image Upload */}
            <div style={{
              background: '#ffffff',
              borderRadius: '16px',
              padding: isMobile ? '16px' : '20px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}>
              <div>
                <h3 style={{
                  fontSize: '1.05em',
                  color: '#1e293b',
                  marginTop: 0,
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span>📷</span> ภาพถ่ายใบเสร็จ / เอกสาร
                </h3>

                {importedEngine && (importedEngine === 'local_server' || importedEngine === 'synology-nas' || importedEngine === 'gemini_spark') && (
                  <div style={{
                    marginBottom: '12px',
                    padding: '8px 12px',
                    background: '#eef2ff',
                    border: '1px solid #c7d2fe',
                    borderRadius: '8px',
                    color: '#4338ca',
                    fontSize: '0.82em',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <span>⚡</span>
                    <span>ข้อมูลนำเข้าจาก Local AI Server (Mac M4 / Synology NAS)</span>
                  </div>
                )}

                {receiptImage ? (
                  <div style={{ position: 'relative', textAlign: 'center', marginBottom: '12px' }}>
                    <div
                      onClick={() => setZoomedImageUrl(receiptImage)}
                      style={{
                        position: 'relative',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        cursor: 'zoom-in',
                        border: '2px solid #10b981',
                        maxHeight: '220px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#f1f5f9'
                      }}
                    >
                      <img
                        src={receiptImage}
                        alt="Receipt Document"
                        style={{ maxWidth: '100%', maxHeight: '220px', objectFit: 'contain' }}
                      />
                      <span style={{
                        position: 'absolute',
                        bottom: '8px',
                        right: '8px',
                        background: 'rgba(0,0,0,0.65)',
                        color: '#ffffff',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        fontSize: '0.75em',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        🔍 แตะเพื่อซูม
                      </span>
                    </div>

                    <button
                      type="button"
                      onClick={() => setIsOcrModalOpen(true)}
                      style={{
                        marginTop: '10px',
                        width: '100%',
                        padding: '10px',
                        background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '0.92em',
                        boxShadow: '0 4px 10px rgba(16, 185, 129, 0.25)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                    >
                      <span>✨</span>
                      <span>🔍 สแกน OCR ใบเสร็จ</span>
                    </button>

                    <label
                      style={{
                        marginTop: '8px',
                        width: '100%',
                        padding: '9px 12px',
                        background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '0.88em',
                        boxShadow: '0 4px 10px rgba(99, 102, 241, 0.25)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        boxSizing: 'border-box'
                      }}
                      title="นำเข้าไฟล์ .json ที่ประมวลผลโดย Local AI Server บน Mac M4 หรือ Synology NAS"
                    >
                      <span>⚡</span>
                      <span>นำเข้า JSON (Local AI / NAS)</span>
                      <input
                        type="file"
                        accept=".json,application/json"
                        onChange={handleJsonFileInputChange}
                        style={{ display: 'none' }}
                      />
                    </label>

                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      style={{
                        marginTop: '8px',
                        width: '100%',
                        padding: '8px',
                        background: '#fee2e2',
                        color: '#b91c1c',
                        border: '1px solid #fca5a5',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '0.85em',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px'
                      }}
                    >
                      🗑️ ลบรูปถ่ายนี้
                    </button>
                  </div>
                ) : (
                  <div style={{
                    border: '2px dashed #cbd5e1',
                    borderRadius: '12px',
                    padding: '24px 16px',
                    textAlign: 'center',
                    background: '#f8fafc',
                    marginBottom: '12px'
                  }}>
                    {isUploadingImage ? (
                      <div style={{ padding: '20px 0' }}>
                        <div style={{
                          display: 'inline-block',
                          width: '32px',
                          height: '32px',
                          border: '4px solid #cbd5e1',
                          borderTopColor: '#0ba360',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }} />
                        <p style={{ margin: '10px 0 0 0', fontSize: '0.88em', color: '#64748b' }}>กำลังอัปโหลดและบีบอัดภาพ...</p>
                      </div>
                    ) : (
                      <>
                        <div style={{ fontSize: '2.5em', marginBottom: '8px' }}>🧾</div>
                        <p style={{ margin: '0 0 12px 0', fontSize: '0.88em', color: '#64748b' }}>
                          ถ่ายรูปใบเสร็จ หรือเลือกรูปจากคลังภาพ
                        </p>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                          {/* File / Gallery button */}
                          <label style={{
                            padding: '8px 14px',
                            background: '#0ba360',
                            color: '#ffffff',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '0.85em',
                            fontWeight: 'bold',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            📂 เลือกไฟล์
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleImageFileChange}
                              style={{ display: 'none' }}
                            />
                          </label>

                          {/* Camera direct capture */}
                          <label style={{
                            padding: '8px 14px',
                            background: '#0284c7',
                            color: '#ffffff',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '0.85em',
                            fontWeight: 'bold',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            📸 ถ่ายภาพ
                            <input
                              type="file"
                              accept="image/*"
                              capture="environment"
                              onChange={handleImageFileChange}
                              style={{ display: 'none' }}
                            />
                          </label>

                          {/* JSON Import from Local AI / Synology NAS */}
                          <label style={{
                            padding: '8px 14px',
                            background: '#6366f1',
                            color: '#ffffff',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '0.85em',
                            fontWeight: 'bold',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            boxShadow: '0 2px 6px rgba(99, 102, 241, 0.25)'
                          }} title="นำเข้าไฟล์ .json จากโฟลเดอร์ Local บน Mac หรือ Synology NAS">
                            ⚡ นำเข้า JSON
                            <input
                              type="file"
                              accept=".json,application/json"
                              onChange={handleJsonFileInputChange}
                              style={{ display: 'none' }}
                            />
                          </label>
                        </div>
                      </>
                    )}

                    {uploadError && (
                      <p style={{ color: '#ef4444', fontSize: '0.8em', margin: '8px 0 0 0' }}>{uploadError}</p>
                    )}
                  </div>
                )}
              </div>

              <div style={{ fontSize: '0.78em', color: '#64748b', background: '#f8fafc', padding: '8px 12px', borderRadius: '8px' }}>
                💡 <strong>คำแนะนำ:</strong> ภาพถ่ายใบเสร็จจะถูกเก็บบันทึกบนระบบคลาวด์เพื่อการตรวจสอบย้อนหลังทางบัญชีและการตรวจรับพัสดุ
              </div>
            </div>
          </div>

          {/* ========================================== */}
          {/* Section: Dynamic Line Items Table */}
          {/* ========================================== */}
          <div style={{
            background: '#ffffff',
            borderRadius: '16px',
            padding: isMobile ? '16px' : '24px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
            marginBottom: '20px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
              borderBottom: '1px solid #f1f5f9',
              paddingBottom: '12px',
              flexWrap: 'wrap',
              gap: '10px'
            }}>
              <div>
                <h2 style={{
                  fontSize: '1.2em',
                  color: '#1e293b',
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span>📦</span> รายการวัสดุที่ตรวจรับ ({items.length} รายการ)
                </h2>
                <p style={{ margin: '4px 0 0 0', fontSize: '0.85em', color: '#64748b' }}>
                  กรอก Lot Number และวันหมดอายุ (EXP) เพื่อความปลอดภัยสูงสุดของเวชภัณฑ์ทางการแพทย์
                </p>
              </div>

              <button
                type="button"
                onClick={handleAddItem}
                style={{
                  padding: '9px 18px',
                  background: '#0ba360',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '0.9em',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 2px 4px rgba(11, 163, 96, 0.2)'
                }}
              >
                ➕ เพิ่มรายการวัสดุ
              </button>
            </div>

            {/* Desktop Table View */}
            {!isMobile ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', color: '#475569', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>
                      <th style={{ padding: '12px 10px', width: '40px', textAlign: 'center' }}>#</th>
                      <th style={{ padding: '12px 10px', minWidth: '220px' }}>ชื่อรายการวัสดุ <span style={{ color: '#ef4444' }}>*</span></th>
                      <th style={{ padding: '12px 10px', minWidth: '130px' }}>
                        Lot Number <span style={{ background: '#fef3c7', color: '#d97706', padding: '2px 6px', borderRadius: '4px', fontSize: '0.75em' }}>สำคัญ</span>
                      </th>
                      <th style={{ padding: '12px 10px', width: '140px' }}>วันหมดอายุ (EXP)</th>
                      <th style={{ padding: '12px 10px', width: '140px' }}>วันผลิต (MFG)</th>
                      <th style={{ padding: '12px 10px', width: '90px' }}>จำนวน</th>
                      <th style={{ padding: '12px 10px', width: '100px' }}>หน่วยนับ</th>
                      <th style={{ padding: '12px 10px', width: '110px' }}>ราคา/หน่วย (฿)</th>
                      <th style={{ padding: '12px 10px', width: '120px', textAlign: 'right' }}>รวมเงิน (฿)</th>
                      <th style={{ padding: '12px 10px', width: '50px', textAlign: 'center' }}>ลบ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, idx) => {
                      const rowTotal = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
                      return (
                        <tr key={it.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '10px', textAlign: 'center', color: '#94a3b8' }}>{idx + 1}</td>
                          
                          {/* Item Name */}
                          <td style={{ padding: '10px' }}>
                            <input
                              type="text"
                              list={`item-list-${it.id}`}
                              required
                              placeholder="ค้นหาหรือพิมพ์ชื่อวัสดุ"
                              value={it.item_name}
                              onChange={(e) => handleItemChange(idx, 'item_name', e.target.value)}
                              style={{
                                width: '100%',
                                padding: '8px 10px',
                                borderRadius: '6px',
                                border: '1px solid #cbd5e1',
                                fontSize: '0.95em',
                                boxSizing: 'border-box'
                              }}
                            />
                            <datalist id={`item-list-${it.id}`}>
                              {itemNameSuggestions.map((name, i) => (
                                <option key={i} value={name} />
                              ))}
                            </datalist>
                          </td>

                          {/* Lot Number */}
                          <td style={{ padding: '10px' }}>
                            <input
                              type="text"
                              placeholder="เช่น LOT2409"
                              value={it.lot_number}
                              onChange={(e) => handleItemChange(idx, 'lot_number', e.target.value)}
                              style={{
                                width: '100%',
                                padding: '8px 10px',
                                borderRadius: '6px',
                                border: '1px solid #cbd5e1',
                                fontSize: '0.95em',
                                boxSizing: 'border-box'
                              }}
                            />
                          </td>

                          {/* Expiry Date */}
                          <td style={{ padding: '10px' }}>
                            <input
                              type="date"
                              value={it.exp_date}
                              onChange={(e) => handleItemChange(idx, 'exp_date', e.target.value)}
                              style={{
                                width: '100%',
                                padding: '8px 8px',
                                borderRadius: '6px',
                                border: '1px solid #cbd5e1',
                                fontSize: '0.9em',
                                boxSizing: 'border-box'
                              }}
                            />
                          </td>

                          {/* MFG Date */}
                          <td style={{ padding: '10px' }}>
                            <input
                              type="date"
                              value={it.mfg_date}
                              onChange={(e) => handleItemChange(idx, 'mfg_date', e.target.value)}
                              style={{
                                width: '100%',
                                padding: '8px 8px',
                                borderRadius: '6px',
                                border: '1px solid #cbd5e1',
                                fontSize: '0.9em',
                                boxSizing: 'border-box'
                              }}
                            />
                          </td>

                          {/* Quantity */}
                          <td style={{ padding: '10px' }}>
                            <input
                              type="number"
                              min="0.01"
                              step="any"
                              value={it.quantity}
                              onChange={(e) => handleItemChange(idx, 'quantity', e.target.value)}
                              style={{
                                width: '100%',
                                padding: '8px 10px',
                                borderRadius: '6px',
                                border: '1px solid #cbd5e1',
                                fontSize: '0.95em',
                                boxSizing: 'border-box',
                                textAlign: 'right'
                              }}
                            />
                          </td>

                          {/* Unit */}
                          <td style={{ padding: '10px' }}>
                            <input
                              type="text"
                              placeholder="กล่อง, ชิ้น..."
                              value={it.unit}
                              onChange={(e) => handleItemChange(idx, 'unit', e.target.value)}
                              style={{
                                width: '100%',
                                padding: '8px 10px',
                                borderRadius: '6px',
                                border: '1px solid #cbd5e1',
                                fontSize: '0.95em',
                                boxSizing: 'border-box'
                              }}
                            />
                          </td>

                          {/* Unit Price */}
                          <td style={{ padding: '10px' }}>
                            <input
                              type="number"
                              min="0"
                              step="any"
                              value={it.unit_price}
                              onChange={(e) => handleItemChange(idx, 'unit_price', e.target.value)}
                              style={{
                                width: '100%',
                                padding: '8px 10px',
                                borderRadius: '6px',
                                border: '1px solid #cbd5e1',
                                fontSize: '0.95em',
                                boxSizing: 'border-box',
                                textAlign: 'right'
                              }}
                            />
                          </td>

                          {/* Row Total */}
                          <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold', color: '#0f172a' }}>
                            ฿{rowTotal.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>

                          {/* Delete Action */}
                          <td style={{ padding: '10px', textAlign: 'center' }}>
                            <button
                              type="button"
                              onClick={() => handleRemoveItem(idx)}
                              title="ลบแถวนี้"
                              style={{
                                background: '#fee2e2',
                                color: '#b91c1c',
                                border: 'none',
                                borderRadius: '6px',
                                width: '32px',
                                height: '32px',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.9em'
                              }}
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              /* Mobile Card View */
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {items.map((it, idx) => {
                  const rowTotal = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
                  return (
                    <div key={it.id} style={{
                      background: '#f8fafc',
                      borderRadius: '12px',
                      padding: '14px',
                      border: '1px solid #e2e8f0',
                      position: 'relative'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <span style={{ fontWeight: 'bold', color: '#0ba360', fontSize: '0.9em' }}>
                          รายการที่ #{idx + 1}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(idx)}
                          style={{
                            background: '#fee2e2',
                            color: '#b91c1c',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '4px 8px',
                            cursor: 'pointer',
                            fontSize: '0.8em'
                          }}
                        >
                          🗑️ ลบ
                        </button>
                      </div>

                      <div style={{ marginBottom: '10px' }}>
                        <label style={{ display: 'block', fontSize: '0.82em', fontWeight: 'bold', color: '#475569', marginBottom: '4px' }}>
                          ชื่อรายการวัสดุ <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        <input
                          type="text"
                          list={`item-list-m-${it.id}`}
                          required
                          placeholder="ชื่อวัสดุ"
                          value={it.item_name}
                          onChange={(e) => handleItemChange(idx, 'item_name', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px 10px',
                            borderRadius: '6px',
                            border: '1px solid #cbd5e1',
                            fontSize: '0.95em',
                            boxSizing: 'border-box'
                          }}
                        />
                        <datalist id={`item-list-m-${it.id}`}>
                          {itemNameSuggestions.map((name, i) => (
                            <option key={i} value={name} />
                          ))}
                        </datalist>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '10px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.82em', fontWeight: 'bold', color: '#475569', marginBottom: '4px' }}>
                            Lot Number
                          </label>
                          <input
                            type="text"
                            placeholder="เช่น LOT2409"
                            value={it.lot_number}
                            onChange={(e) => handleItemChange(idx, 'lot_number', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '8px',
                              borderRadius: '6px',
                              border: '1px solid #cbd5e1',
                              fontSize: '0.9em',
                              boxSizing: 'border-box'
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.82em', fontWeight: 'bold', color: '#475569', marginBottom: '4px' }}>
                            วันหมดอายุ (EXP)
                          </label>
                          <input
                            type="date"
                            value={it.exp_date}
                            onChange={(e) => handleItemChange(idx, 'exp_date', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '8px',
                              borderRadius: '6px',
                              border: '1px solid #cbd5e1',
                              fontSize: '0.88em',
                              boxSizing: 'border-box'
                            }}
                          />
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '10px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.82em', fontWeight: 'bold', color: '#475569', marginBottom: '4px' }}>
                            จำนวน
                          </label>
                          <input
                            type="number"
                            min="0.01"
                            step="any"
                            value={it.quantity}
                            onChange={(e) => handleItemChange(idx, 'quantity', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '8px',
                              borderRadius: '6px',
                              border: '1px solid #cbd5e1',
                              fontSize: '0.9em',
                              boxSizing: 'border-box'
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.82em', fontWeight: 'bold', color: '#475569', marginBottom: '4px' }}>
                            หน่วยนับ
                          </label>
                          <input
                            type="text"
                            placeholder="กล่อง, ชิ้น"
                            value={it.unit}
                            onChange={(e) => handleItemChange(idx, 'unit', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '8px',
                              borderRadius: '6px',
                              border: '1px solid #cbd5e1',
                              fontSize: '0.9em',
                              boxSizing: 'border-box'
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.82em', fontWeight: 'bold', color: '#475569', marginBottom: '4px' }}>
                            ราคา/หน่วย (฿)
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="any"
                            value={it.unit_price}
                            onChange={(e) => handleItemChange(idx, 'unit_price', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '8px',
                              borderRadius: '6px',
                              border: '1px solid #cbd5e1',
                              fontSize: '0.9em',
                              boxSizing: 'border-box'
                            }}
                          />
                        </div>
                      </div>

                      <div style={{ textAlign: 'right', fontSize: '0.9em', fontWeight: 'bold', color: '#0ba360', paddingTop: '4px' }}>
                        รวมเงิน: ฿{rowTotal.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Grand Total Summary Bar */}
            <div style={{
              marginTop: '20px',
              padding: '16px 20px',
              background: '#f8fafc',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '15px'
            }}>
              <div>
                <span style={{ fontSize: '0.9em', color: '#64748b' }}>สรุปจำนวนตรวจรับ:</span>
                <span style={{ marginLeft: '8px', fontWeight: 'bold', color: '#1e293b' }}>
                  {items.length} รายการ ({totalItemQuantity.toLocaleString()} ชิ้น/หน่วย)
                </span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '1.05em', color: '#334155', fontWeight: 'bold' }}>ยอดเงินรวมทั้งสิ้น:</span>
                <span style={{
                  fontSize: '1.5em',
                  fontWeight: 'bold',
                  color: '#0ba360',
                  background: '#ecfdf5',
                  padding: '4px 12px',
                  borderRadius: '8px'
                }}>
                  ฿{grandTotal.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>

            {/* Submit Button */}
            <div style={{ marginTop: '24px', textAlign: 'right' }}>
              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  padding: isMobile ? '12px 24px' : '14px 36px',
                  background: isSubmitting ? '#94a3b8' : 'linear-gradient(135deg, #0ba360 0%, #3cba92 100%)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold',
                  fontSize: isMobile ? '1em' : '1.1em',
                  boxShadow: '0 4px 12px rgba(11, 163, 96, 0.35)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  transition: 'transform 0.1s'
                }}
              >
                {isSubmitting ? '⏳ กำลังบันทึกข้อมูล...' : '💾 บันทึกรับเข้าและอัปเดตสต็อก'}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* ============================================================ */}
      {/* TAB 2: ประวัติใบเสร็จและการตรวจรับ (Archive & History) */}
      {/* ============================================================ */}
      {activeTab === 'history' && (
        <div style={{
          background: '#ffffff',
          borderRadius: '16px',
          padding: isMobile ? '16px' : '24px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
        }}>
          {/* Search and Refresh Bar */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <div style={{ flex: '1', minWidth: '260px' }}>
              <input
                type="text"
                placeholder="🔍 ค้นหาเลขที่ใบเสร็จ, PO, บริษัท หรือชื่อวัสดุ..."
                value={historySearchQuery}
                onChange={(e) => setHistorySearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  borderRadius: '10px',
                  border: '1.5px solid #cbd5e1',
                  fontSize: '0.95em',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <button
              type="button"
              onClick={fetchReceiptsHistory}
              style={{
                padding: '10px 16px',
                background: '#f1f5f9',
                color: '#334155',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '0.9em',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              🔄 รีเฟรชข้อมูล
            </button>
          </div>

          {isLoadingHistory ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#64748b' }}>
              <div style={{
                display: 'inline-block',
                width: '36px',
                height: '36px',
                border: '4px solid #cbd5e1',
                borderTopColor: '#0ba360',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              <p style={{ marginTop: '12px' }}>กำลังโหลดประวัติใบเสร็จ...</p>
            </div>
          ) : filteredReceipts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
              <div style={{ fontSize: '3em', marginBottom: '10px' }}>📑</div>
              <p style={{ fontSize: '1.05em', color: '#64748b', margin: 0 }}>
                {historySearchQuery ? 'ไม่พบใบเสร็จที่ตรงกับคำค้นหา' : 'ยังไม่มีประวัติใบเสร็จที่บันทึกไว้'}
              </p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(350px, 1fr))',
              gap: '16px'
            }}>
              {filteredReceipts.map(rc => {
                const itemCount = rc.receipt_items?.length || 0;
                return (
                  <div
                    key={rc.id}
                    onClick={() => setSelectedReceiptDetail(rc)}
                    style={{
                      background: '#ffffff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '14px',
                      padding: '16px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.03)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#0ba360';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 12px rgba(11, 163, 96, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e2e8f0';
                      e.currentTarget.style.transform = 'none';
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.03)';
                    }}
                  >
                    <div>
                      {/* Card Header: Receipt No & Date */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <div>
                          <span style={{
                            background: '#ecfdf5',
                            color: '#065f46',
                            fontWeight: 'bold',
                            padding: '4px 8px',
                            borderRadius: '6px',
                            fontSize: '0.88em'
                          }}>
                            #{rc.receipt_number}
                          </span>
                          {rc.po_number && (
                            <span style={{ marginLeft: '6px', color: '#64748b', fontSize: '0.8em' }}>
                              PO: {rc.po_number}
                            </span>
                          )}
                          {(() => {
                            const q = historySearchQuery.toLowerCase().trim();
                            const hasOcrMatch = q && (
                              rc.ocr_text?.toLowerCase().includes(q) ||
                              (rc.note && rc.note.includes('[OCR Text]') && rc.note.toLowerCase().includes(q))
                            );
                            if (hasOcrMatch) {
                              return (
                                <span style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  background: '#ecfdf5',
                                  color: '#059669',
                                  border: '1px solid #a7f3d0',
                                  borderRadius: '6px',
                                  padding: '2px 8px',
                                  fontSize: '0.75em',
                                  fontWeight: '600',
                                  marginLeft: '6px'
                                }}>
                                  🔍 ตรงกับข้อความในใบเสร็จ
                                </span>
                              );
                            }
                            return null;
                          })()}
                        </div>
                        <span style={{ fontSize: '0.82em', color: '#64748b' }}>
                          📅 {rc.received_date || '-'}
                        </span>
                      </div>

                      {/* Supplier */}
                      <div style={{ fontSize: '0.95em', fontWeight: 'bold', color: '#1e293b', marginBottom: '6px' }}>
                        🏢 {rc.supplier_name || 'ไม่ระบุผู้จำหน่าย'}
                      </div>

                      {/* Summary text */}
                      <div style={{ fontSize: '0.85em', color: '#64748b', marginBottom: '10px' }}>
                        📦 ตรวจรับ: <strong>{itemCount} รายการ</strong>
                        {rc.note && <span style={{ marginLeft: '8px' }}>• {rc.note}</span>}
                      </div>
                    </div>

                    {/* Card Footer: Image preview thumbnail + Price */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderTop: '1px solid #f1f5f9',
                      paddingTop: '10px',
                      marginTop: '10px'
                    }}>
                      <div>
                        {rc.receipt_image_url ? (
                          <span style={{ fontSize: '0.78em', color: '#0284c7', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            🖼️ มีรูปถ่ายเอกสาร
                          </span>
                        ) : (
                          <span style={{ fontSize: '0.78em', color: '#94a3b8' }}>
                            ไม่มีรูปแนบ
                          </span>
                        )}
                      </div>

                      <div style={{ textAlign: 'right' }}>
                        <span style={{ fontSize: '1.1em', fontWeight: 'bold', color: '#0ba360' }}>
                          ฿{(Number(rc.total_amount) || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>

                    {/* Button: สรุปตรวจรับ & ออก QR */}
                    {onNavigateToInspection && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onNavigateToInspection(rc.id);
                        }}
                        style={{
                          marginTop: '10px',
                          width: '100%',
                          padding: '8px 12px',
                          backgroundColor: '#0284c7',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '0.86em',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          boxShadow: '0 2px 4px rgba(2, 132, 199, 0.2)'
                        }}
                      >
                        📋 สรุปตรวจรับ & ออก QR
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ============================================================ */}
      {/* MODAL: รายละเอียดใบเสร็จ (Receipt Detail Modal) */}
      {/* ============================================================ */}
      {selectedReceiptDetail && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.7)',
          backdropFilter: 'blur(6px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
          padding: '16px'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '20px',
            width: '100%',
            maxWidth: '840px',
            maxHeight: '90vh',
            overflowY: 'auto',
            padding: isMobile ? '16px' : '28px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            border: '1px solid #e2e8f0'
          }}>
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              borderBottom: '1px solid #f1f5f9',
              paddingBottom: '16px',
              marginBottom: '20px'
            }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <h2 style={{ margin: 0, fontSize: '1.3em', color: '#0f172a' }}>
                    ใบเสร็จเลขที่ #{selectedReceiptDetail.receipt_number}
                  </h2>
                  <span style={{
                    background: '#ecfdf5',
                    color: '#065f46',
                    padding: '2px 8px',
                    borderRadius: '6px',
                    fontSize: '0.8em',
                    fontWeight: 'bold'
                  }}>
                    {selectedReceiptDetail.status || 'completed'}
                  </span>
                </div>
                <p style={{ margin: '4px 0 0 0', fontSize: '0.88em', color: '#64748b' }}>
                  บันทึกโดย: {selectedReceiptDetail.created_by || '-'} • วันที่ตรวจรับ: {selectedReceiptDetail.received_date || '-'}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setSelectedReceiptDetail(null)}
                style={{
                  background: '#f1f5f9',
                  border: 'none',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  cursor: 'pointer',
                  fontSize: '1.1em',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#64748b'
                }}
              >
                ✕
              </button>
            </div>

            {/* Info Summary Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: '12px',
              background: '#f8fafc',
              padding: '14px',
              borderRadius: '12px',
              marginBottom: '20px',
              fontSize: '0.9em'
            }}>
              <div>
                <div style={{ color: '#64748b', fontSize: '0.82em' }}>ผู้จัดจำหน่าย:</div>
                <div style={{ fontWeight: 'bold', color: '#1e293b' }}>{selectedReceiptDetail.supplier_name || '-'}</div>
              </div>
              <div>
                <div style={{ color: '#64748b', fontSize: '0.82em' }}>เลขที่ PO:</div>
                <div style={{ fontWeight: 'bold', color: '#1e293b' }}>{selectedReceiptDetail.po_number || '-'}</div>
              </div>
              <div>
                <div style={{ color: '#64748b', fontSize: '0.82em' }}>ยอดเงินรวมทั้งสิ้น:</div>
                <div style={{ fontWeight: 'bold', color: '#0ba360', fontSize: '1.1em' }}>
                  ฿{(Number(selectedReceiptDetail.total_amount) || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
            </div>

            {/* Receipt Image Preview */}
            {selectedReceiptDetail.receipt_image_url && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
                  <h4 style={{ margin: 0, fontSize: '0.95em', color: '#334155' }}>
                    🖼️ ภาพถ่ายเอกสารที่แนบมา:
                  </h4>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={() => setZoomedImageUrl(selectedReceiptDetail.receipt_image_url)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 12px',
                        backgroundColor: '#eff6ff',
                        color: '#1d4ed8',
                        border: '1px solid #bfdbfe',
                        borderRadius: '6px',
                        fontSize: '0.85em',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      🔍 ขยายดูภาพต้นฉบับ
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDownloadReceiptImage(selectedReceiptDetail.receipt_image_url, `receipt-${selectedReceiptDetail.receipt_number || 'scan'}.jpg`)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 12px',
                        backgroundColor: '#f0fdf4',
                        color: '#15803d',
                        border: '1px solid #bbf7d0',
                        borderRadius: '6px',
                        fontSize: '0.85em',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      ⬇️ ดาวน์โหลดรูปใบเสร็จ
                    </button>
                  </div>
                </div>
                <div
                  onClick={() => setZoomedImageUrl(selectedReceiptDetail.receipt_image_url)}
                  style={{
                    maxHeight: '220px',
                    borderRadius: '10px',
                    overflow: 'hidden',
                    cursor: 'zoom-in',
                    border: '1px solid #cbd5e1',
                    background: '#f1f5f9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <img
                    src={selectedReceiptDetail.receipt_image_url}
                    alt="Receipt Scan"
                    style={{ maxWidth: '100%', maxHeight: '220px', objectFit: 'contain' }}
                  />
                </div>
              </div>
            )}

            {/* OCR Extracted Text Section */}
            {(() => {
              const displayOcrText = selectedReceiptDetail.ocr_text || (
                selectedReceiptDetail.note && selectedReceiptDetail.note.includes('[OCR Text]:')
                  ? selectedReceiptDetail.note.split('[OCR Text]:').slice(1).join('[OCR Text]:').trim()
                  : ''
              );
              if (!displayOcrText) return null;
              return (
                <div style={{
                  marginBottom: '20px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '10px',
                  padding: '14px'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '10px',
                    flexWrap: 'wrap',
                    gap: '8px'
                  }}>
                    <h4 style={{ margin: 0, fontSize: '0.95em', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>📄</span> ข้อความที่อ่านได้จากภาพใบเสร็จ (OCR Extracted Text)
                    </h4>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        type="button"
                        onClick={() => {
                          if (navigator?.clipboard?.writeText) {
                            navigator.clipboard.writeText(displayOcrText);
                          }
                          alert('คัดลอกข้อความเรียบร้อยแล้ว');
                        }}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '5px 10px',
                          backgroundColor: '#f1f5f9',
                          color: '#334155',
                          border: '1px solid #cbd5e1',
                          borderRadius: '6px',
                          fontSize: '0.8em',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        📋 คัดลอกข้อความ
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          downloadTextFile(
                            `receipt-${selectedReceiptDetail.receipt_number || 'ocr'}.txt`,
                            displayOcrText
                          );
                        }}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '5px 10px',
                          backgroundColor: '#eff6ff',
                          color: '#1d4ed8',
                          border: '1px solid #bfdbfe',
                          borderRadius: '6px',
                          fontSize: '0.8em',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        💾 ดาวน์โหลดเป็นไฟล์ .txt
                      </button>
                    </div>
                  </div>
                  <pre style={{
                    margin: 0,
                    padding: '10px',
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '0.82em',
                    fontFamily: 'monospace',
                    color: '#334155',
                    maxHeight: '160px',
                    overflowY: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}>
                    {displayOcrText}
                  </pre>
                </div>
              );
            })()}

            {/* Line Items Table */}
            <h4 style={{ margin: '0 0 10px 0', fontSize: '1em', color: '#1e293b' }}>
              📦 รายการสินค้าในใบเสร็จ:
            </h4>
            <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '10px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88em' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', color: '#475569', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>
                    <th style={{ padding: '10px 12px' }}>ชื่อรายการ</th>
                    <th style={{ padding: '10px 12px' }}>Lot Number</th>
                    <th style={{ padding: '10px 12px' }}>วันหมดอายุ (EXP)</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right' }}>จำนวน</th>
                    <th style={{ padding: '10px 12px' }}>หน่วย</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right' }}>ราคา/หน่วย (฿)</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right' }}>รวมเงิน (฿)</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedReceiptDetail.receipt_items || []).map((it, idx) => (
                    <tr key={it.id || idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '10px 12px', fontWeight: '500' }}>{it.item_name}</td>
                      <td style={{ padding: '10px 12px' }}>
                        {it.lot_number ? (
                          <span style={{ background: '#fef3c7', color: '#92400e', padding: '2px 6px', borderRadius: '4px', fontSize: '0.85em' }}>
                            {it.lot_number}
                          </span>
                        ) : '-'}
                      </td>
                      <td style={{ padding: '10px 12px' }}>{it.exp_date || '-'}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 'bold' }}>
                        {(Number(it.quantity) || 0).toLocaleString()}
                      </td>
                      <td style={{ padding: '10px 12px', color: '#64748b' }}>{it.unit || 'ชิ้น'}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                        {(Number(it.unit_price) || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 'bold', color: '#0ba360' }}>
                        {(Number(it.total_price) || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Modal Actions */}
            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              {onNavigateToInspection && (
                <button
                  type="button"
                  onClick={() => {
                    const id = selectedReceiptDetail.id;
                    setSelectedReceiptDetail(null);
                    onNavigateToInspection(id);
                  }}
                  style={{
                    padding: '10px 18px',
                    background: '#0284c7',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '0.9em',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  📋 สรุปตรวจรับ & ออก QR
                </button>
              )}
              <button
                type="button"
                onClick={() => setSelectedReceiptDetail(null)}
                style={{
                  padding: '10px 22px',
                  background: '#334155',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '0.9em'
                }}
              >
                ปิดหน้าต่าง
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* MODAL: Zoom High-Res Image (Full Resolution) */}
      {/* ============================================================ */}
      {zoomedImageUrl && (
        <div
          onClick={() => setZoomedImageUrl(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.88)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'zoom-out',
            padding: '20px'
          }}
        >
          <img
            src={zoomedImageUrl}
            alt="Zoomed Receipt"
            style={{
              maxWidth: '95vw',
              maxHeight: '95vh',
              objectFit: 'contain',
              borderRadius: '8px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5)'
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              display: 'flex',
              gap: '10px',
              alignItems: 'center'
            }}
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleDownloadReceiptImage(zoomedImageUrl, `receipt-${selectedReceiptDetail?.receipt_number || 'scan'}.jpg`);
              }}
              style={{
                background: 'rgba(255, 255, 255, 0.95)',
                color: '#15803d',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 14px',
                cursor: 'pointer',
                fontSize: '0.88em',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)'
              }}
              title="ดาวน์โหลดรูปภาพเก็บไว้เป็นหลักฐาน"
            >
              ⬇️ ดาวน์โหลดรูปใบเสร็จ
            </button>
            <button
              type="button"
              onClick={() => setZoomedImageUrl(null)}
              style={{
                background: 'rgba(255, 255, 255, 0.25)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                cursor: 'pointer',
                fontSize: '1.2em',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* MODAL: Receipt OCR & Auto-Fill */}
      {/* ============================================================ */}
      <ReceiptOcrModal
        isOpen={isOcrModalOpen}
        onClose={() => setIsOcrModalOpen(false)}
        imageUrl={receiptImage}
        onApplyData={handleApplyOcrData}
        supplierSuggestions={supplierSuggestions}
      />
    </div>
  );
};

export default StockInReceiptPage;
